"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Info,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatAbsolute, formatUtc, timeAgo } from "./webhooks/format";
import { highlightJson, prettyJson } from "./webhooks/jsonHighlight";

// ────────────────────────────────────────────────────────────────────────────
// Shared UI kit for the Developers pages (Webhooks first).
//
// Section / FormField / SubGroup / PageHeader / inputCls are COPIES of the
// file-local primitives in ApiSettingsPage.tsx, and CodeBlock extends the
// one in DocumentationPage.tsx — class strings are kept byte-identical so
// the new pages are indistinguishable from the existing ones. Those two
// files keep their private copies for now.
//
// Rules every component here follows:
//   • text from a merchant's server is only ever rendered as React text
//     children (no HTML injection path exists in this file);
//   • nothing relies on hover (touch) or on horizontal scrolling (the app
//     hides all scrollbars globally);
//   • no toasts — callers decide what deserves one.
// ────────────────────────────────────────────────────────────────────────────

// ─── Class-string constants ────────────────────────────────────────────────
export const inputCls =
  "w-full px-3 py-2.5 rounded-xl bg-gray-900/40 border border-gray-800 focus:border-[#61CE70]/50 focus:outline-none text-white text-sm placeholder-gray-600";

/** Solid green call-to-action (ApiSettingsPage "Save" idiom). */
export const primaryBtn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-60 text-black text-sm font-semibold transition-colors";
/** Tinted green button — the Section `action` slot idiom ("New key"). */
export const secondaryBtn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#61CE70]/10 hover:bg-[#61CE70]/15 text-[#61CE70] text-xs font-semibold border border-[#61CE70]/25 transition-colors disabled:opacity-60";
/** Borderless cancel button (ConfirmModal idiom). */
export const ghostBtn =
  "px-4 py-2 rounded-xl text-gray-300 hover:bg-white/5 text-sm font-medium transition-colors disabled:opacity-40";
/** Solid red destructive button (ConfirmModal variant="danger" idiom). */
export const dangerBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 bg-red-500 hover:bg-red-600 text-white";
/** Neutral icon button with a 44px touch target (`p-2.5` around a `w-4` icon). */
export const iconBtn =
  "shrink-0 p-2.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors";
/** Keyboard focus ring for custom controls (native buttons keep the UA ring). */
export const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#61CE70]/60";

// ─── Layout primitives (copies) ────────────────────────────────────────────

/**
 * Card wrapper for one panel of a page.
 * @param icon     node for the 36px tile — house usage: `<Send className="w-4 h-4 text-[#61CE70]" />`
 * @param title    panel title
 * @param desc     optional one-line description under the title
 * @param action   optional node on the right of the header (usually a `secondaryBtn`)
 * @param children panel body
 */
export function Section({
  icon,
  title,
  desc,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  desc?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold">{title}</p>
          {desc && <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{desc}</p>}
        </div>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

/**
 * Labelled form field. The caption + control live in one `<label>` (clicking
 * the caption focuses the control); helper and error text sit OUTSIDE the
 * label so they do not pollute the control's accessible name.
 * @param label    uppercase caption
 * @param children the control (`<input className={inputCls} …/>`)
 * @param hint     optional helper text under the control
 * @param error    optional error text; rendered in red with `role="alert"`
 * @param errorId  id put on the error node — pass the same value as the
 *                 control's `aria-describedby` (and set `aria-invalid`)
 */
export function FormField({
  label,
  children,
  hint,
  error,
  errorId,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  errorId?: string;
}) {
  return (
    <div>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-gray-500 font-semibold mb-1.5">
          {label}
        </span>
        {children}
      </label>
      {hint && !error && <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="text-red-300 text-xs mt-1.5">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Inset group inside a Section: heading + optional description + fields.
 */
export function SubGroup({
  heading,
  description,
  children,
}: {
  heading: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl bg-gray-900/30 border border-gray-800/60 p-4 space-y-3">
      <div>
        <p className="text-white text-sm font-semibold">{heading}</p>
        {description && (
          <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Page title block ("CASHTAP DEVELOPERS / Webhooks").
 * @param icon    node for the 44px tile — `<Send className="w-5 h-5 text-[#61CE70]" />`
 * @param title   the h1 (string, or a node such as host + health pill)
 * @param eyebrow small green caption above the title; default "CashTap Developers"
 * @param blurb   optional paragraph under the header
 * @param action  optional node on the right (e.g. a "Docs" link)
 */
export function PageHeader({
  icon,
  title,
  eyebrow = "CashTap Developers",
  blurb,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  eyebrow?: string;
  blurb?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#61CE70]/20 via-[#50B060]/10 to-transparent border border-[#61CE70]/30 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#61CE70] font-semibold">{eyebrow}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{title}</h1>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {blurb && <p className="mt-3 text-gray-400 text-sm max-w-2xl">{blurb}</p>}
    </div>
  );
}

// ─── Clipboard ─────────────────────────────────────────────────────────────

// Copies `text`; resolves true on success. Falls back to a hidden textarea
// when the async Clipboard API is missing or refuses (insecure context,
// denied permission). Never throws and never logs the text. File-local on
// purpose: everything outside copies through <CopyButton>.
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// House copy idiom: swap the icon for a green Check for 1500 ms. No toast.
function useCopied(): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const copy = (text: string) => {
    copyToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    });
  };
  return [copied, copy];
}

/**
 * Always-visible copy button (never hover-only — unusable on touch).
 * @param text      the exact string to copy
 * @param label     accessible name + tooltip, e.g. "Copy delivery id"
 * @param variant   "icon" (default; 44px target) or "text" (icon + "Copy"/"Copied")
 * @param className extra classes appended to the button
 * Safe inside a clickable row/link: the click does not bubble or navigate.
 */
export function CopyButton({
  text,
  label = "Copy",
  variant = "icon",
  className = "",
}: {
  text: string;
  label?: string;
  variant?: "icon" | "text";
  className?: string;
}) {
  const [copied, copy] = useCopied();
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copy(text);
  };
  if (variant === "text") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors ${className}`}
        title={label}
        aria-label={copied ? "Copied" : label}
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5 text-[#61CE70]" />
            <span className="text-[#61CE70]">Copied</span>
          </>
        ) : (
          <>
            <Copy className="w-3.5 h-3.5" />
            <span>Copy</span>
          </>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${iconBtn} ${className}`}
      title={label}
      aria-label={copied ? "Copied" : label}
    >
      {copied ? <Check className="w-4 h-4 text-[#61CE70]" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

// ─── CodeBlock ─────────────────────────────────────────────────────────────

/**
 * Code / payload viewer (DocumentationPage CodeBlock + JSON colouring,
 * wrapping and collapse).
 * @param language          header caption ("json", "http", "response")
 * @param code              the raw text
 * @param highlight         pretty-print + colour when `code` parses as JSON;
 *                          anything else is shown untouched, monochrome
 * @param wrap              wrap long lines (`whitespace-pre-wrap break-all`) —
 *                          use for anything that came from a merchant's server
 * @param collapsedMaxLines collapse to a fixed height with a "Show all n
 *                          lines" footer when the text is longer than this
 * @param copyText          what Copy copies; defaults to `code` EXACTLY as
 *                          passed (never the prettified text) so signed
 *                          bytes stay byte-accurate
 */
export function CodeBlock({
  language,
  code,
  highlight = false,
  wrap = false,
  collapsedMaxLines,
  copyText,
}: {
  language: string;
  code: string;
  highlight?: boolean;
  wrap?: boolean;
  collapsedMaxLines?: number;
  copyText?: string;
}) {
  const [copied, copy] = useCopied();
  const [expanded, setExpanded] = useState(false);

  const { text, body, lineCount } = useMemo(() => {
    const pretty = highlight ? prettyJson(code) : { text: code, isJson: false };
    return {
      text: pretty.text,
      body: pretty.isJson ? highlightJson(pretty.text) : pretty.text,
      lineCount: pretty.text.split("\n").length,
    };
  }, [code, highlight]);

  // A minified payload is ONE very long line that still wraps into a tall
  // block, so length counts as well as newlines (~80 chars per visual line).
  const collapsible =
    !!collapsedMaxLines &&
    (lineCount > collapsedMaxLines || (wrap && text.length > collapsedMaxLines * 80));
  const collapsed = collapsible && !expanded;

  return (
    <div className="my-3 rounded-xl bg-[#0a0d12] border border-gray-800 overflow-hidden">
      {/* Header bar — language label + always-visible copy button. A
          hover-only copy button is invisible and unusable on touch
          (hover-vs-tap), so it stays visible at all times here. */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-800/80 bg-white/[0.02]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">
          {language}
        </span>
        <button
          type="button"
          onClick={() => copy(copyText ?? code)}
          className="inline-flex items-center gap-1.5 px-2 py-1 -mr-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-[#61CE70]" />
              <span className="text-[#61CE70]">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <div className={collapsed ? "relative max-h-[28rem] overflow-hidden" : "relative"}>
        <pre
          className={`p-4 overflow-x-auto text-[13px] text-gray-200 font-mono leading-relaxed${
            wrap ? " whitespace-pre-wrap break-all" : ""
          }`}
        >
          <code>{body}</code>
        </pre>
        {collapsed && (
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0a0d12] to-transparent pointer-events-none"
          />
        )}
      </div>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="w-full py-2 text-[11px] font-medium text-gray-400 hover:text-white border-t border-gray-800/80"
        >
          {expanded ? "Collapse" : lineCount > 1 ? `Show all ${lineCount} lines` : "Show all"}
        </button>
      )}
    </div>
  );
}

// ─── Modals ────────────────────────────────────────────────────────────────
// Open modals form a stack: only the top one reacts to Esc / Tab, and the
// body scroll lock is released when the LAST one closes — whatever order
// they close in. Mutated from effects only (no load-time side effects).
const modalStack: string[] = [];
let bodyOverflowBeforeLock = "";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

// Behaviour shared by DevModal and ConfirmDialog. Rendered in a portal on
// <body>: the app shell scrolls inside <main>, so an in-place overlay
// would neither lock that scroll nor reliably sit above the sticky header.
function ModalShell({
  open,
  onClose,
  busy,
  labelledBy,
  cardClassName,
  children,
}: {
  open: boolean;
  onClose: () => void;
  busy: boolean;
  labelledBy: string;
  cardClassName: string;
  children: ReactNode;
}) {
  const id = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const pressStartedOnBackdrop = useRef(false);
  // Latest props for the document listener, so the effect below runs once
  // per open (re-running it would steal focus back on every parent render).
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement as HTMLElement | null;
    const card = cardRef.current;

    modalStack.push(id);
    if (modalStack.length === 1) {
      bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    // Initial focus: an explicitly marked control (ConfirmDialog's Cancel —
    // the safe default, even when the body holds a checkbox or a link),
    // else the first form field (DevModal forms), else the first control
    // that is not the Close button, else the card itself.
    if (card) {
      const items = focusablesIn(card);
      const target =
        items.find((el) => el.hasAttribute("data-modal-initial-focus")) ||
        items.find((el) => /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) ||
        items.find((el) => !el.hasAttribute("data-modal-close")) ||
        card;
      target.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (modalStack[modalStack.length - 1] !== id) return;
      if (e.key === "Escape") {
        if (!busyRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !card) return;
      const items = focusablesIn(card);
      if (items.length === 0) {
        e.preventDefault();
        card.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Focus escaped the dialog, or sits on a control that was just
      // disabled by `busy` — pull it back in.
      if (!active || !card.contains(active) || (active as HTMLButtonElement).disabled) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === card)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      const at = modalStack.indexOf(id);
      if (at !== -1) modalStack.splice(at, 1);
      if (modalStack.length === 0) document.body.style.overflow = bodyOverflowBeforeLock;
      // Give focus back to whatever opened the dialog.
      if (trigger && typeof trigger.focus === "function" && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [open, id]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
      onMouseDown={(e) => {
        pressStartedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // React events bubble through portals to the React parent (a card,
        // a row link): nothing inside the dialog may leak out.
        e.stopPropagation();
        // Close only on a real backdrop click — not when a text selection
        // that started inside the card ends out here.
        if (e.target === e.currentTarget && pressStartedOnBackdrop.current && !busy) onClose();
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-busy={busy || undefined}
        tabIndex={-1}
        className={`${cardClassName} focus:outline-none`}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          data-modal-close=""
          className="absolute top-2 right-2 p-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>,
    document.body,
  );
}

const MODAL_SIZE = { md: "max-w-md", lg: "max-w-lg", "2xl": "max-w-2xl" } as const;

/**
 * Accessible modal for forms and rich content.
 * `role="dialog"`, focus trap (Tab / Shift+Tab cycle), initial focus on the
 * first field, focus restored to the trigger on close, body scroll locked.
 * While `busy`, Esc, backdrop click and the X are ALL blocked — also disable
 * your own Cancel button with `disabled={busy}`.
 * Unmounts its children when closed, so modal state (and any secret held in
 * it) is dropped.
 * @param open     whether the modal is shown
 * @param onClose  called by Esc, backdrop, X (never while busy)
 * @param title    dialog title (also its accessible name)
 * @param icon     optional node for the 44px tile, e.g. `<Send className="w-5 h-5" />`
 * @param size     "md" (default, max-w-md) | "lg" (max-w-lg) | "2xl" (max-w-2xl)
 * @param busy     a request is in flight — blocks every way of closing
 * @param children body
 * @param footer   optional right-aligned button row (`ghostBtn` Cancel + `primaryBtn`)
 */
export function DevModal({
  open,
  onClose,
  title,
  icon,
  size = "md",
  busy = false,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  size?: "md" | "lg" | "2xl";
  busy?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const titleId = useId();
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      busy={busy}
      labelledBy={titleId}
      cardClassName={`relative w-full ${MODAL_SIZE[size]} max-h-[88vh] overflow-y-auto rounded-2xl border border-gray-800 bg-gradient-to-br from-[#0F1419] to-[#0B0F16] shadow-2xl`}
    >
      <div className="p-6 sm:p-7">
        <div className="flex items-start gap-3 pr-8">
          {icon && (
            <div className="w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 bg-[#61CE70]/10 border-[#61CE70]/25 text-[#61CE70]">
              {icon}
            </div>
          )}
          <div className={`min-w-0 flex-1 ${icon ? "pt-2" : ""}`}>
            <h2 id={titleId} className="text-white font-bold text-lg leading-snug">
              {title}
            </h2>
          </div>
        </div>
        <div className="mt-5">{children}</div>
        {footer && <div className="mt-6 flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </ModalShell>
  );
}

/**
 * Confirmation dialog — ConfirmModal's API on top of the DevModal behaviour
 * (focus trap, focus restore, scroll lock, everything blocked while busy),
 * plus a `children` body slot for notices, checkboxes or a countdown.
 * Initial focus lands on Cancel.
 * @param onConfirm       called by the confirm button (ignored while busy / disabled)
 * @param description     plain-text body
 * @param children        optional rich body rendered under the description
 * @param confirmLabel    default "Confirm"
 * @param cancelLabel     default "Cancel"
 * @param variant         "default" (green) | "danger" (red)
 * @param busy            shows a spinner on confirm and blocks closing
 * @param icon            node for the tile; default AlertTriangle
 * @param confirmDisabled disables confirm without the busy state (e.g. a 429 countdown)
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  busy = false,
  icon,
  confirmDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
  busy?: boolean;
  icon?: ReactNode;
  confirmDisabled?: boolean;
}) {
  const titleId = useId();
  const accent =
    variant === "danger"
      ? { btn: "bg-red-500 hover:bg-red-600 text-white", ring: "border-red-500/30", tile: "bg-red-500/10 border-red-500/25 text-red-400" }
      : { btn: "bg-[#61CE70] hover:bg-[#50B060] text-black", ring: "border-[#61CE70]/30", tile: "bg-[#61CE70]/10 border-[#61CE70]/25 text-[#61CE70]" };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      busy={busy}
      labelledBy={titleId}
      cardClassName={`relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border ${accent.ring} bg-gradient-to-br from-[#0F1419] to-[#0B0F16] shadow-2xl`}
    >
      <div className="p-6 sm:p-7">
        <div className="flex items-start gap-3 pr-6">
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${accent.tile}`}>
            {icon || <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 id={titleId} className="text-white font-bold text-lg leading-snug">
              {title}
            </h2>
            {description && (
              <p className="mt-1.5 text-gray-400 text-sm leading-relaxed">{description}</p>
            )}
          </div>
        </div>

        {children && <div className="mt-4">{children}</div>}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-modal-initial-focus=""
            className="px-4 py-2 rounded-xl text-gray-300 hover:bg-white/5 text-sm font-medium transition-colors disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!busy && !confirmDisabled) onConfirm();
            }}
            disabled={busy || confirmDisabled}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${accent.btn}`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Controls ──────────────────────────────────────────────────────────────

/**
 * On/off switch — `<button role="switch">`. Controlled: it never flips
 * itself, the parent owns `checked` (optimistic or not is the parent's call).
 * @param checked  current state
 * @param onChange receives the requested next state
 * @param label    accessible name, e.g. "Deliveries enabled" (visible text,
 *                 if any, is the caller's)
 * @param disabled non-interactive
 * @param busy     request in flight: non-interactive + spinner in the thumb
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  busy = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      // The ::before pads the 24px track to a 44px tall touch target.
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors ${
        checked ? "bg-[#61CE70] border-[#61CE70]" : "bg-gray-800 border-gray-700"
      } disabled:opacity-60 disabled:cursor-not-allowed before:absolute before:-inset-y-2.5 before:inset-x-0 before:content-[''] ${focusRing}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-[18px] w-[18px] rounded-full bg-white transition-transform flex items-center justify-center ${
          checked ? "translate-x-5" : ""
        }`}
      >
        {busy && <Loader2 className="w-3 h-3 text-gray-500 animate-spin" />}
      </span>
    </button>
  );
}

/**
 * Two-to-four option toggle (WalletPage segmented idiom), e.g.
 * [Live events | Test events] or [Pretty | Raw].
 * @param value     selected option value
 * @param onChange  receives the clicked option value
 * @param options   `{ value, label }[]`
 * @param ariaLabel accessible name of the group
 * @param fullWidth stretch to the container on mobile (`w-full sm:w-auto`)
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  fullWidth = false,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
  fullWidth?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex items-center bg-gray-900/60 border border-gray-800/80 rounded-xl p-1${
        fullWidth ? " w-full sm:w-auto" : ""
      }`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={`${fullWidth ? "flex-1 sm:flex-none " : ""}px-4 h-9 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
            value === opt.value ? "bg-[#61CE70] text-black shadow-sm" : "text-gray-400 hover:text-white"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Pills, tags, tiles ────────────────────────────────────────────────────

/** One entry of a status vocabulary (see webhooks/vocab.ts). */
export interface PillUi {
  label: string;
  /** background + text + ring colour classes */
  cls: string;
  icon: LucideIcon;
  /** spin the icon (Loader2) */
  spin?: boolean;
}

/**
 * Ring pill — ALWAYS icon + text, never colour alone.
 * @param ui    vocabulary entry: `DELIVERY_STATUS_UI[d.status]`, `healthUi(endpoint)`
 * @param label overrides `ui.label` (e.g. `deliveryStatusLabel(d)`)
 * @param size  "sm" (default, list rows) | "lg" (detail page headers)
 */
export function StatusPill({
  ui,
  label,
  size = "sm",
  className = "",
}: {
  ui: PillUi;
  label?: string;
  size?: "sm" | "lg";
  className?: string;
}) {
  const Icon = ui.icon;
  const lg = size === "lg";
  return (
    <span
      className={`inline-flex items-center gap-1 ${
        lg ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[10px]"
      } rounded-full font-bold ring-1 whitespace-nowrap ${ui.cls} ${className}`}
    >
      <Icon size={lg ? 15 : 13} strokeWidth={2.5} className={ui.spin ? "animate-spin" : undefined} aria-hidden="true" />
      {label ?? ui.label}
    </span>
  );
}

const TAG_TONE = {
  blue: "text-blue-300 bg-blue-500/15 border-blue-500/30",
  amber: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  red: "text-red-300 bg-red-500/15 border-red-500/30",
  gray: "text-gray-300 bg-gray-500/15 border-gray-500/30",
} as const;

/**
 * Small square uppercase tag (TEST, RESEND).
 * @param tone "blue" (default) | "amber" | "red" | "gray"
 */
export function Tag({
  children,
  tone = "blue",
}: {
  children: ReactNode;
  tone?: keyof typeof TAG_TONE;
}) {
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded-md border ${TAG_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Key-figure tile (docs `Stat` idiom). Lay out in
 * `grid grid-cols-2 sm:grid-cols-4 gap-2`.
 * @param label uppercase caption
 * @param value the figure (mono, tabular-nums)
 * @param tone  colours the value: "default" white | "green" | "amber" | "red"
 */
export function StatTile({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  tone?: "default" | "green" | "amber" | "red";
}) {
  const color =
    tone === "green"
      ? "text-[#61CE70]"
      : tone === "amber"
        ? "text-amber-300"
        : tone === "red"
          ? "text-red-400"
          : "text-white";
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-3 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-500">{label}</p>
      <p className={`${color} font-mono text-sm mt-1.5 tabular-nums truncate`}>{value}</p>
    </div>
  );
}

// ─── States ────────────────────────────────────────────────────────────────

/**
 * Dashed empty state (KeysPanel idiom).
 * @param icon   lucide icon component (not a node): `icon={Send}`
 * @param title  first line
 * @param hint   optional second, dimmer line
 * @param action optional node under the text (a `primaryBtn` button or Link)
 */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon: LucideIcon;
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-700/60 bg-gray-900/20 p-6 text-center">
      <Icon className="w-5 h-5 mx-auto text-gray-600 mb-2" aria-hidden="true" />
      <p className="text-gray-400 text-sm">{title}</p>
      {hint && <p className="text-gray-600 text-xs mt-1">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Red "couldn't load" card with a Retry link (ApiSettingsPage idiom).
 * @param title     e.g. "Couldn't load webhooks"
 * @param message   the error's message (`e.message`)
 * @param onRetry   shows the Retry button when provided
 * @param requestId optional "req_…" printed small for support
 */
export function ErrorCard({
  title,
  message,
  onRetry,
  requestId,
}: {
  title: string;
  message?: string | null;
  onRetry?: () => void;
  requestId?: string | null;
}) {
  return (
    <div
      role="alert"
      className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 flex items-start gap-3"
    >
      <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-red-200 font-semibold">{title}</p>
        {message && <p className="text-red-300/80 text-sm mt-1 break-words">{message}</p>}
        {requestId && (
          <p className="text-red-300/60 text-[11px] font-mono mt-1 break-all">Request ID · {requestId}</p>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-200 hover:text-white"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        )}
      </div>
    </div>
  );
}

const NOTICE_TONE = {
  // The quiet grey note used inside panels (KeysPanel security notice).
  neutral: {
    box: "flex items-start gap-2.5 p-3 rounded-xl border border-gray-800/80 bg-gray-900/30",
    icon: "w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5",
    title: "text-gray-200 text-[12px] font-semibold",
    body: "text-[12px] text-gray-400 leading-relaxed",
    Icon: Info,
  },
  info: {
    box: "flex items-start gap-3 p-4 rounded-xl border border-blue-500/30 bg-blue-500/5",
    icon: "w-4 h-4 text-blue-400 shrink-0 mt-0.5",
    title: "text-blue-100 font-semibold text-sm",
    body: "text-blue-200/80 text-sm leading-relaxed",
    Icon: Info,
  },
  warn: {
    box: "flex items-start gap-3 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5",
    icon: "w-4 h-4 text-amber-400 shrink-0 mt-0.5",
    title: "text-amber-100 font-semibold text-sm",
    body: "text-amber-200/80 text-sm leading-relaxed",
    Icon: AlertTriangle,
  },
  error: {
    box: "flex items-start gap-3 p-4 rounded-xl border border-red-500/30 bg-red-500/5",
    icon: "w-4 h-4 text-red-400 shrink-0 mt-0.5",
    title: "text-red-200 font-semibold text-sm",
    body: "text-red-300/80 text-sm leading-relaxed",
    Icon: AlertCircle,
  },
  success: {
    box: "flex items-start gap-3 p-4 rounded-xl border border-[#61CE70]/30 bg-[#61CE70]/5",
    icon: "w-4 h-4 text-[#61CE70] shrink-0 mt-0.5",
    title: "text-white font-semibold text-sm",
    body: "text-gray-400 text-sm leading-relaxed",
    Icon: CheckCircle2,
  },
} as const;

/**
 * Inline notice inside a page or panel.
 * @param tone     "neutral" (default; small grey note) | "info" | "warn" | "error" | "success"
 * @param icon     lucide icon component overriding the tone's default (`icon={Lock}`)
 * @param title    optional bold first line
 * @param children body text / nodes
 * @param action   optional row of buttons / links under the body
 * @param role     "alert" | "status" when the notice appears dynamically
 */
export function InlineNotice({
  tone = "neutral",
  icon,
  title,
  children,
  action,
  role,
  className = "",
}: {
  tone?: keyof typeof NOTICE_TONE;
  icon?: LucideIcon;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  role?: "alert" | "status";
  className?: string;
}) {
  const t = NOTICE_TONE[tone];
  const Icon = icon || t.Icon;
  return (
    <div role={role} className={`${t.box} ${className}`}>
      <Icon className={t.icon} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className={t.title}>{title}</p>}
        {children && <div className={`${t.body}${title ? " mt-1" : ""} break-words`}>{children}</div>}
        {action && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">{action}</div>}
      </div>
    </div>
  );
}

/**
 * First-load placeholder for a delivery list — mirrors the two-line row
 * layout so the page keeps its shape. `aria-hidden`; never show it for a
 * refetch once rows exist.
 * @param rows number of placeholder rows (default 5)
 */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <ul className="divide-y divide-gray-800/50 animate-pulse" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="px-4 sm:px-5 py-3.5">
          <div className="flex items-center gap-3">
            <div className="h-5 w-20 rounded-full bg-gray-800/70 shrink-0" />
            <div className="h-3 w-44 max-w-full rounded bg-gray-800/60" />
            <div className="hidden sm:block h-3 w-28 rounded bg-gray-800/40 ml-auto" />
            <div className="hidden sm:block h-5 w-14 rounded bg-gray-800/60" />
            <div className="hidden sm:block h-3 w-12 rounded bg-gray-800/40" />
          </div>
          <div className="mt-2 h-2.5 w-52 max-w-full rounded bg-gray-800/30" />
        </li>
      ))}
    </ul>
  );
}

// ─── TimeText ──────────────────────────────────────────────────────────────
// One shared 30 s ticker keeps every relative time on screen fresh — a list
// of 100 rows must not run 100 intervals. Started by the first subscriber,
// stopped with the last.
const tickListeners = new Set<() => void>();
let tickTimer: ReturnType<typeof setInterval> | null = null;

function subscribeTick(fn: () => void): () => void {
  tickListeners.add(fn);
  if (!tickTimer) {
    tickTimer = setInterval(() => tickListeners.forEach((l) => l()), 30000);
  }
  return () => {
    tickListeners.delete(fn);
    if (tickListeners.size === 0 && tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}

/**
 * `<time dateTime title>` that shows a relative time ("2m ago", "in 1h 52m")
 * and toggles to the absolute local time on tap / Enter / Space — touch has
 * no hover, so the tooltip alone is not enough. The tooltip always carries
 * local + UTC. Safe inside a clickable row: the tap does not navigate.
 * @param iso         ISO-8601 string; null / invalid renders `fallback`
 * @param initial     "relative" (default) | "absolute"
 * @param interactive false → plain `<time>` with no toggle (default true)
 * @param fallback    text for a missing value (default "—")
 */
export function TimeText({
  iso,
  initial = "relative",
  interactive = true,
  fallback = "—",
  className = "",
}: {
  iso: string | null | undefined;
  initial?: "relative" | "absolute";
  interactive?: boolean;
  fallback?: string;
  className?: string;
}) {
  const [mode, setMode] = useState<"relative" | "absolute">(initial);
  const [, setTick] = useState(0);
  const relative = mode === "relative";

  useEffect(() => {
    if (!relative) return;
    return subscribeTick(() => setTick((n) => n + 1));
  }, [relative]);

  const valid = !!iso && !Number.isNaN(new Date(iso).getTime());
  if (!valid) return <span className={className}>{fallback}</span>;

  const text = relative ? timeAgo(iso) : formatAbsolute(iso);
  const title = `${formatAbsolute(iso)} · ${formatUtc(iso)}`;

  if (!interactive) {
    return (
      <time dateTime={iso as string} title={title} className={`tabular-nums ${className}`}>
        {text}
      </time>
    );
  }

  const toggle = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMode((m) => (m === "relative" ? "absolute" : "relative"));
  };

  return (
    <time
      dateTime={iso as string}
      title={title}
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") toggle(e);
      }}
      className={`tabular-nums cursor-pointer rounded-sm ${focusRing} ${className}`}
    >
      {text}
    </time>
  );
}
